import { message, Modal } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Button from '../../components/Button/Button';
import ContactsTour from '../../components/ContactsTour/ContactsTour';
import InstructionsBadge from '../../components/InstructionsBadge/InstructionsBadge';
import { useTour } from '../../contexts/TourContext';
import { AuthContext } from '../../Guard/AuthContext';
import { useContactStore } from '../../hooks/useContactStore';
import { apiInstance } from '../../utils/api.js';
import { logUserAction } from '../../utils/logger';
import { getBlobFromUrl } from '../../utils/urlImgToBlob.js';
import ContactCard from './components/ContactCard';
import ContactForm from './components/ContactForm';

const ContactPage = () => {
  const { id } = useParams();
  const { currentContactStore } = useContactStore();
  const { user, setUser } = useContext(AuthContext);
  const { isTourActive, startTour } = useTour();

  const { push } = useHistory();
  const isNewContact = !id;
  const [contact, setContact] = useState({});
  const [blobContent, setBlobContent] = useState(null);
  const [cards, setCards] = useState([]);

  const [reference, setReference] = useState({ PlantEmployee: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isReferencesLoaded, setIsReferencesLoaded] = useState(false);
  const [isCardsLoaded, setIsCardsLoaded] = useState(false);
  const [isContactLoaded, setIsContactLoaded] = useState(false);
  const [instructionsRead, setInstructionsRead] = useState(user?.instructionsRead || false);
  const [isDismissModalVisible, setIsDismissModalVisible] = useState(false);
  const [isDismissLoading, setIsDismissLoading] = useState(false);

  const handleDismissClick = () => {
    if (contact?.isCardOrdered) return;
    setIsDismissModalVisible(true);
  };

  const handleDismissConfirm = async () => {
    if (!contact?.Id || contact?.isCardOrdered) return;
    setIsDismissLoading(true);
    try {
      await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/post-data?type=dismiss-contact`,
        'POST',
        {
          contactId: contact.Id,
        }
      );
      logUserAction({
        action: 'DISMISS_EMPLOYEE_SUCCESS',
        level: 'info',
        message: `Успішно звільнено співробітника: ${contact?.RespName || contact?.name || contact?.Id}`,
        storeCrmId: currentContactStore?.crmId,
        details: { contactId: contact.Id, name: contact?.RespName },
      });
      message.success('Співробітника успішно звільнено');
      setIsDismissModalVisible(false);
      push('/contacts');
    } catch (error) {
      console.error('Помилка звільнення співробітника:', error);
      message.error(error.message || 'Помилка звільнення співробітника');
    } finally {
      setIsDismissLoading(false);
    }
  };

  // Синхронізація з user при зміні
  useEffect(() => {
    if (user?.instructionsRead !== undefined) {
      setInstructionsRead(user.instructionsRead);
    }
  }, [user?.instructionsRead]);

  // Функція для відмітки інструкції як прочитаної
  const markInstructionsAsRead = async () => {
    try {
      await apiInstance(`${process.env.REACT_APP_API_URL}/api/users/${user.id}`, 'PUT', {
        instructionsRead: true,
      });
      setInstructionsRead(true);
      // Оновлюємо користувача в контексті
      setUser({ ...user, instructionsRead: true });
      message.success('Інструкцію відмічено як прочитану');
    } catch (error) {
      console.error('Помилка оновлення статусу інструкції:', error);
      message.error('Не вдалося оновити статус інструкції');
    }
  };

  const removePhoto = () => {
    setContact((prev) => ({
      ...prev,
      attachFile1: null,
      attachFileName1: null,
    }));
    return true;
  };

  const fetchData = useCallback(async () => {
    if (!id) {
      setIsContactLoaded(true);
      return;
    }

    try {
      const response = await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=contacts`
      );

      const foundContact = response.rows.find((e) => e.Id === id);

      if (!foundContact) {
        // message.error('Контакт не знайдено');
        push('/contacts');
        setIsContactLoaded(true);
        return;
      }

      if (foundContact.Photo?.value) {
        const photoUrl = `${process.env.REACT_APP_API_URL}/api/files?fileId=${foundContact.Photo.value}&type=contact`;
        const { blob: photoBlob } = await getBlobFromUrl(photoUrl);
        const reader = new FileReader();
        reader.onloadend = () => {
          setBlobContent(reader.result);
        };
        reader.readAsDataURL(photoBlob);
      }

      let contactCard = null;
      try {
        const cardResponse = await apiInstance(
          `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=contact-card&contactId=${foundContact.Id}`
        );
        contactCard = cardResponse.rows?.length ? cardResponse.rows[0] : null;
        if (
          contactCard &&
          (contactCard.RespSCAStatus?.value === '25405d2a-9df4-47a9-9313-fc8b4ca329fa' ||
            contactCard.RespSCAStatus?.value === 'df17a6d7-2510-4a1e-9692-f9c34d66b156')
        ) {
          contactCard = null;
        }
      } catch (cardError) {
        console.warn('Не удалось получить картку контакта:', cardError);
      }

      let cases = [];
      try {
        const casesResponse = await apiInstance(
          `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=cases`
        );
        cases = casesResponse.rows || [];
      } catch (casesError) {
        console.warn('Не удалось получить заявки:', casesError);
      }

      const IGNORED_STATUSES = [
        '6e5f4218-f46b-1410-fe9a-0050ba5d6c38', // Закрито/Відмовлено
        '3e7f420c-f46b-1410-fc9a-0050ba5d6c38', // Скасовано
      ];
      const contactIdLower = foundContact?.Id?.toLowerCase();

      const isCardOrdered = cases.some((caseItem) => {
        const statusVal = caseItem?.Status?.value;
        if (!statusVal || IGNORED_STATUSES.includes(statusVal)) return false;
        if (!contactIdLower) return false;

        // Старий спосіб: одиночний RespCardReceiver
        const receiverVal = typeof caseItem?.RespCardReceiver === 'string'
          ? caseItem.RespCardReceiver
          : caseItem?.RespCardReceiver?.value;
        if (receiverVal && receiverVal.toLowerCase() === contactIdLower) return true;

        // Contact поле
        const contactVal = typeof caseItem?.Contact === 'string'
          ? caseItem.Contact
          : caseItem?.Contact?.value;
        if (contactVal && contactVal.toLowerCase() === contactIdLower) return true;

        // Новий спосіб: масив RespCardReceivers (із RespCardInCase)
        if (Array.isArray(caseItem?.RespCardReceivers)) {
          if (caseItem.RespCardReceivers.some(
            (rid) => typeof rid === 'string' && rid.toLowerCase() === contactIdLower
          )) return true;
        }

        // Деталь RespCardInCase
        if (Array.isArray(caseItem?.RespCardInCase)) {
          if (caseItem.RespCardInCase.some(
            (c) => c?.id && c.id.toLowerCase() === contactIdLower
          )) return true;
        }

        return false;
      });

      const respNameSegments = (foundContact.RespName || '').trim().split(/\s+/).filter(Boolean);
      const [firstNameFromRespName, ...restRespName] = respNameSegments;
      const derivedFirstName = firstNameFromRespName || '';
      const derivedSurname = restRespName.join(' ') || '';

      const mappedContact = {
        ...foundContact,
        RespName:
          foundContact.GivenName ||
          derivedFirstName ||
          (foundContact.Name ? foundContact.Name.split(' ')[0] : '') ||
          '',
        RespSurName: derivedSurname || foundContact.Surname || '',
        RespMobilePhone: foundContact.MobilePhone,
        RespEmail: foundContact.Email,
        RespCardNumber: contactCard?.Name || '',
        RespCardId: contactCard?.Id || '',
        RespCardStatus: contactCard?.RespSCAStatus || null,
        isCardOrdered,
        RespEmployeeJobId: {
          value: foundContact.Job?.value,
          displayValue: foundContact.Job?.displayValue,
        },
      };

      setContact(mappedContact);
      setIsContactLoaded(true);
    } catch (error) {
      console.error('Помилка завантаження:', error);
      setIsContactLoaded(true);
    }
  }, [id, push]);

  const fetchReferences = async () => {
    try {
      const references = await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/requests/references`,
        'GET'
      );
      setReference(references);
      setIsReferencesLoaded(true);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      message.error('Ошибка получения данных');
      setIsReferencesLoaded(true);
    }
  };

  const loadCards = useCallback(async () => {
    try {
      const resp = await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=cards`
      );
      setCards(resp.rows || []);
      setIsCardsLoaded(true);
    } catch (e) {
      console.warn('Ошибка загрузки карток:', e);
      setIsCardsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setIsContactLoaded(true);
    }
    fetchReferences();
    loadCards();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentContactStore]);

  // Проверяем, загружены ли все необходимые данные
  useEffect(() => {
    if (isReferencesLoaded && isCardsLoaded && isContactLoaded) {
      setIsLoading(false);
    }
  }, [isReferencesLoaded, isCardsLoaded, isContactLoaded]);

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    const cleaned = phoneNumber.toString().replace(/\D/g, '');

    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(
      8,
      10
    )}-${cleaned.slice(10, 12)}`;
  };

  const handleChangePhotoUpload = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }

    if (info.file.status === 'done') {
      const reader = new FileReader();
      reader.readAsDataURL(info.file.originFileObj);
      reader.onload = () => {
        setContact((prev) => ({
          ...prev,
          attachFile1: reader.result,
          attachFileName1: info.file.name,
        }));
      };
    }
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('Можна завантажувати лише JPG/PNG файли!');
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Розмір зображення не повинен перевищувати 10MB!');
    }
    return isJpgOrPng && isLt10M;
  };

  const handleSubmit = async (contactData, phoneExists = false) => {
    // Объединяем имя и фамилию в одну строку
    const fullName = `${contactData.RespName || ''} ${contactData.RespSurName || ''}`.trim();

    try {
      if (isNewContact) {
        // Создание нового контакта
        const payloadCreate = {
          RespName: contactData.RespName, // Имя
          RespSurName: contactData.RespSurName, // Фамилия
          RespName2: fullName, // Полное имя (имя + фамилия)
          RespMobilePhone: contactData.RespMobilePhone,
          RespEmail: contactData.RespEmail,
          RespEmployeeJob: contactData.RespEmployeeJob,
          attachFile1: contact.attachFile1,
          attachFileName1: contact.attachFileName1,
          phoneExists, // Передаем информацию о том, что телефон уже существует
        };

        await apiInstance(
          `${process.env.REACT_APP_API_URL}/api/post-data?type=create-contact`,
          'POST',
          payloadCreate
        );

        logUserAction({
          action: 'CREATE_CONTACT_SUCCESS',
          level: 'info',
          message: `Створено співробітника: ${fullName}`,
          storeCrmId: currentContactStore?.crmId,
          details: { name: fullName, email: contactData.RespEmail, phone: contactData.RespMobilePhone },
        });

        message.success('Контакт створено');
        push('/contacts');
      } else {
        // Редактирование существующего контакта
        const payloadUpdate = {
          Id: contact.Id, // ID контакта для обновления
          RespName: contactData.RespName, // Имя
          RespSurName: contactData.RespSurName, // Фамилия
          RespName2: fullName, // Полное имя (имя + фамилия)
          RespMobilePhone: contactData.RespMobilePhone,
          RespEmail: contactData.RespEmail,
          RespEmployeeJob: contactData.RespEmployeeJob,
          attachFile1: contact.attachFile1,
          attachFileName1: contact.attachFileName1,
          phoneExists, // Передаем информацию о том, что телефон уже существует
        };

        // Обновляем контакт
        await apiInstance(
          `${process.env.REACT_APP_API_URL}/api/post-data?type=update-card`,
          'POST',
          payloadUpdate
        );

        logUserAction({
          action: 'UPDATE_CONTACT_SUCCESS',
          level: 'info',
          message: `Оновлено співробітника: ${fullName}`,
          storeCrmId: currentContactStore?.crmId,
          details: { contactId: contact.Id, name: fullName },
        });

        message.success('Дані оновлено');
        push('/contacts');
      }
    } catch (error) {
      logUserAction({
        action: isNewContact ? 'CREATE_CONTACT_ERROR' : 'UPDATE_CONTACT_ERROR',
        level: 'error',
        message: error?.message || 'Помилка при збереженні контакту',
        storeCrmId: currentContactStore?.crmId,
        details: { error: String(error) },
      });
      console.error('Помилка відправки:', error);
      message.error(error.message || 'Помилка збереження');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContact((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoUpload = (file) => {
    if (beforeUpload(file)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setContact((prev) => ({
          ...prev,
          attachFile1: e.target.result,
          attachFileName1: file.name,
        }));
      };
      reader.readAsDataURL(file);
    }
    return false;
  };

  const positions = Array.isArray(reference.PlantEmployee) ? reference.PlantEmployee : [];

  // Убран глобальный лоадер - теперь используем локальные скелетоны
  if (!contact && id) return <div>Контакт не знайдений</div>;

  return (
    <>
      <div className="box boxes request-item-header">
        <div className="request-item-header__inner">
          <span className="request-item-header__title">
            {isNewContact ? 'Додати співробітника' : 'Дії з картками'}
          </span>

          {/* Інструкція - показується тільки якщо не прочитана */}
          <InstructionsBadge
            instructionsRead={instructionsRead}
            onClick={() => {
              // Переходим к таблице перед запуском тура
              push('/contacts');
              setTimeout(() => {
                startTour();
                // НЕ отправляем запрос здесь - только при завершении тура
              }, 300);
            }}
            alwaysVisible={true}
          />
        </div>
        {/* <div className="cards-available">
          <span className="text-grey-dark-normal-f22-l28">Доступно карток: </span>
          <span className="text-black-bold-f24">
            <span style={{ color: 'rgb(219, 62, 177)' }}>{freeCards}</span>з {totalCards}
          </span>
        </div> */}
      </div>
      <div className="employee">
        <div className="box employee-box">
          <div className="employee-header__avatar-container">
            <img
              className="employee-header__avatar"
              src={blobContent || contact?.attachFile1 || '/images/profile-avatar.png'}
              alt="avatar"
            />
            <div className="employee-header__info">
              <div className="employee-header__name">
                {`${contact.RespName || ''} ${contact.RespSurName || ''}`.trim() || contact.Name}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <div className="employee-header__email">{contact?.RespEmail || contact?.Email}</div>
                <div className="employee-header__phone">
                  {formatPhoneNumber(contact?.RespMobilePhone || contact?.MobilePhone)}
                </div>
                <div className="employee-header__email">
                  {isNewContact
                    ? contact?.position?.name
                    : contact?.RespEmployeeJobId?.displayValue || contact?.Job?.displayValue}
                </div>
              </div>
            </div>
          </div>
          <div className="contact-form-row">
            <ContactForm
              contact={contact || {}}
              onDismiss={handleDismissClick}
              isDismissLoading={isDismissLoading}
              beforeUpload={handlePhotoUpload}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              handlePositionChange={(position) =>
                setContact((prev) => ({
                  ...prev,
                  RespEmployeeJobId: position
                    ? { value: position.Id, displayValue: position.Name }
                    : {},
                  RespEmployeeJob: position ? { Id: position.Id, name: position.Name } : undefined,
                }))
              }
              positions={positions}
              removePhoto={removePhoto}
              handleChangePhotoUpload={handleChangePhotoUpload}
              isNewContact={isNewContact}
              references={reference}
            />
            {(!isNewContact || isTourActive) && (
              <ContactCard
                contact={
                  isTourActive && isNewContact
                    ? {
                        Id: 'demo-id',
                        RespName: 'Тестовий',
                        RespSurName: 'Співробітник',
                        RespCardId: 'demo-card-id',
                        RespCardNumber: 'Картка №12345678',
                        RespCardStatus: {
                          value: '3075a30b-f6e8-45cd-990b-28399db75400', // ASSIGNED_STATUS_ID
                        },
                      }
                    : contact
                }
                isNewContact={false}
                cards={
                  isTourActive && isNewContact
                    ? [
                        {
                          Id: 'demo-card-id',
                          Name: 'Картка №12345678',
                          CardNumber: '12345678',
                          RespSCAStatus: {
                            value: '3075a30b-f6e8-45cd-990b-28399db75400',
                          },
                        },
                      ]
                    : cards
                }
                references={reference}
                onReloadCards={loadCards}
                onContactReload={fetchData}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>

      {/* Інтерактивний тур */}
      {isTourActive && (
        <ContactsTour instructionsRead={instructionsRead} onFinish={markInstructionsAsRead} />
      )}

      <Modal
        className="success-modal"
        visible={isDismissModalVisible}
        onCancel={() => setIsDismissModalVisible(false)}
        footer={
          <div className="ant-btn-wrapper">
            <Button
              onClick={() => setIsDismissModalVisible(false)}
              text="Відмінити"
              bg="orange"
              disabled={isDismissLoading}
            />
            <Button
              text="Звільнити"
              bg="turquoise"
              onClick={handleDismissConfirm}
              loading={isDismissLoading}
              disabled={isDismissLoading}
            />
          </div>
        }
      >
        <div className="success-modal__wrapperSCA">
          <div className="text-black-medium-f22-l30">Звільнення співробітника</div>
          <div className="text-grey-dark-normal-f16-l28">
            Ви впевнені, що хочете звільнити співробітника{' '}
            <span style={{ fontWeight: 'bold' }}>
              {contact.RespName || contact.Name || ''}
            </span>
            ?
            <br />
            Після підтвердження співробітник зникне із загального списку.
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ContactPage;
